import {
    ModalButtonStyles,
    OpenModal,
    useEnum,
    useTable,
} from '@composables/common'
import FileUploadButton from '@partials/actions/FileUploadButton.vue'
import { Badge } from '@src/components'
import { SignatureDocumentType, SignatureType } from '@src/enums'
import { EmissionSignature } from '@src/types'
import {
    confirmSignatureForOrder,
    uploadSignedDocumentForSignature,
} from '@src/utils/api/orders'
import { UploadFileResponse } from '@src/utils/api/s3'
import { useToastStore } from '@src/utils/stores/toast'
import moment from 'moment'
import { Ref, computed, h } from 'vue'
import { useI18n } from 'vue-i18n'

const { getEnumLabel } = useEnum()

export function useSignaturesTable(
    signatures: Ref<EmissionSignature[]>,
    orderUid: Ref<string>,
    openModal: OpenModal,
    isModalOpen: Ref<boolean>,
    reload: () => Promise<void>
) {
    const i18n = useI18n()
    const toast = useToastStore()

    const total = computed(() => {
        return signatures.value.length
    })

    const { TableWrapper: SignaturesTable } = useTable({
        tableProps: {
            id: 'signatures-table',
            label: 'signatures',
            help: 'help_signatures_table',
            total: total,
            data: signatures,
            columns: [
                {
                    label: 'document_type',
                    key: 'document_type',
                    sorting: false,
                    cellRenderer: (props: { rowData: EmissionSignature }) => {
                        return getEnumLabel(
                            SignatureDocumentType,
                            props.rowData.document_type
                        )
                    },
                },
                {
                    label: 'signature_type',
                    key: 'type',
                    sorting: false,
                    cellRenderer: (props: { rowData: EmissionSignature }) => {
                        return getEnumLabel(SignatureType, props.rowData.type)
                    },
                },
                {
                    label: 'signature_date',
                    key: 'signature_date',
                    sorting: false,
                    cellRenderer: (props: { rowData: EmissionSignature }) => {
                        if (props.rowData.signed_at) {
                            return moment(props.rowData.signed_at).format(
                                'DD.MM.yyyy HH:mm'
                            )
                        }
                        // Otherwise-identical to a row with nothing uploaded at all -
                        // this is the only signal distinguishing the two in this table.
                        if (props.rowData.has_pending_upload) {
                            return h(Badge, { type: 'warning' }, () =>
                                i18n.t('signature_pending_review')
                            )
                        }
                    },
                },
                {
                    label: 'signature_cancellation_date',
                    key: 'signature_cancellation_date',
                    sorting: false,
                    cellRenderer: (props: { rowData: EmissionSignature }) => {
                        if (props.rowData.cancelled_at) {
                            return moment(props.rowData.cancelled_at).format(
                                'DD.MM.yyyy HH:mm'
                            )
                        }
                        return '-'
                    },
                },
            ],
            actions: [
                {
                    action: handleShowSignature,
                    icon: 'fa-eye',
                    title: 'view_document',
                    condition: (signature: EmissionSignature) => {
                        return !!(
                            signature.link || signature.unsigned_document_link
                        )
                    },
                },
                {
                    action: handleUploadSignedDocument,
                    icon: 'fa-upload',
                    title: 'upload_signed_document',
                    condition: (signature: EmissionSignature) => {
                        return (
                            signature.type === SignatureType.MANUAL &&
                            !signature.cancelled_at &&
                            !signature.signed_at &&
                            !signature.has_pending_upload
                        )
                    },
                },
                {
                    action: handleConfirmSignature,
                    icon: 'fa-check',
                    title: 'confirm_manual_signature',
                    condition: (signature: EmissionSignature) => {
                        return (
                            signature.type === SignatureType.MANUAL &&
                            !signature.cancelled_at &&
                            !signature.signed_at
                        )
                    },
                },
            ],
        },
    })

    function handleShowSignature(signature: EmissionSignature) {
        // The backend now always resolves `link` to whichever document is most relevant
        // (the signed upload once one exists - even before an admin has confirmed it via
        // signed_at - otherwise the blank unsigned template), so trust it first instead of
        // defaulting to the always-present unsigned_document_link.
        const link = signature.link || signature.unsigned_document_link
        window.open(link)
    }

    function handleUploadSignedDocument(signature: EmissionSignature) {
        isModalOpen.value = true
        openModal(
            {
                id: 'upload_signed_document',
                title: 'modal_title_upload_signed_document',
                open: isModalOpen,
                onCancel: () => (isModalOpen.value = false),
            },
            () =>
                h(FileUploadButton, {
                    title: 'signed_document',
                    accept: '.pdf',
                    mode: 'private',
                    deletable: false,
                    onSubmit: async (response: UploadFileResponse) => {
                        await uploadSignedDocumentForSignature(
                            orderUid.value,
                            signature.id,
                            response.key
                        )
                        isModalOpen.value = false
                        toast.success(
                            i18n.t('toast_success_signed_document_uploaded')
                        )
                        await reload()
                    },
                })
        )
    }

    function handleConfirmSignature(signature: EmissionSignature) {
        isModalOpen.value = true
        openModal(
            {
                id: 'order_confirm_signature',
                title: 'modal_title_confirm_signature',
                open: isModalOpen,
                onCancel: () => (isModalOpen.value = false),
                onSubmit: async () => {
                    await confirmSignatureForOrder(orderUid.value, signature.id)
                    isModalOpen.value = false
                    toast.success(i18n.t('toast_success_signature_confirmed'))
                    await reload()
                },
            },
            () => h('p', i18n.t('modal_text_confirm_signature')),
            {
                styles: {
                    submitButton: ModalButtonStyles['BLUE'],
                },
            }
        )
    }

    return {
        SignaturesTable,
    }
}
